/**
 * Employee BIK Tab
 *
 * Per-employee consolidated view of Benefits in Kind. Answers "for THIS person,
 * what is their total estimated annual BIK, and how was each number derived?" -
 * tying the source-oriented tabs (Accommodation, Motor Vehicles, One-Time
 * Benefits) into a single person-oriented rollup.
 *
 * Architecture: LIVE working view. Figures are computed on read from the latest
 * source data (Asset Portal / dormitory / one-time benefits) - never stale. A
 * frozen snapshot is persisted only when HR verifies or locks an employee for
 * IR8A submission. Frozen rows display the locked figure and flag drift if the
 * source has since changed.
 *
 * Design System: Admin Theme (Red)
 */

import React, { useState, useMemo } from 'react';
import LoadingSpinner from '../../../../components/UI/LoadingSpinner';
import {
  Users,
  DollarSign,
  Home,
  Car,
  Gift,
  RefreshCw,
  Eye,
  CheckCircle,
  Lock,
  Info,
  AlertTriangle,
  X,
} from 'lucide-react';
import { StatusBadge } from '../../../../components/UI/StatusBadge';
import { FilterButton } from '../../../../components/UI/FilterButton';
import {
  useLiveBIKRoster,
  useLiveBIKDetail,
  useFreezeBIK,
} from '../../../../hooks/useBIKData';
import Portal from '../../../../components/UI/Portal';
import IconButton from '../../../../components/UI/IconButton';
import DebouncedSearchInput from '../../../../components/UI/DebouncedSearchInput';
import AssetDetailModal from '../../../../components/AssetDetailModal';
import VehicleDerivation from './VehicleDerivation';

const formatMoney = (value) =>
  `$${parseFloat(value || 0).toLocaleString('en-SG', { minimumFractionDigits: 2 })}`;

const EmployeeBIKTab = ({ year }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [detailStaffId, setDetailStaffId] = useState(null);
  const [detailAssetId, setDetailAssetId] = useState(null);

  const { data: roster, isLoading, isFetching, refetch } = useLiveBIKRoster(year);
  const rows = roster?.rows || [];
  const categoryTotals = roster?.category_totals || {};

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      const matchesSearch = !searchTerm ||
        row.staff_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        row.employee_name?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = !filterStatus || row.status === filterStatus;
      return matchesSearch && matchesStatus;
    });
  }, [rows, searchTerm, filterStatus]);

  const summaryCards = [
    {
      title: 'Total Est. Annual BIK',
      value: roster?.total_bik || '0.00',
      icon: DollarSign,
      bgColor: 'bg-red-100',
      iconColor: 'text-red-600',
      subtitle: `${roster?.employee_count || 0} employees with BIK`,
    },
    {
      title: 'Accommodation (d2)',
      value: categoryTotals.d2_accommodation || '0.00',
      icon: Home,
      bgColor: 'bg-purple-100',
      iconColor: 'text-purple-600',
      subtitle: 'Dormitory benefits',
    },
    {
      title: 'Motor Vehicle (d5)',
      value: categoryTotals.d5_motor_vehicle || '0.00',
      icon: Car,
      bgColor: 'bg-green-100',
      iconColor: 'text-green-600',
      subtitle: 'Car benefit (3/7)',
    },
    {
      title: 'One-Time (d4)',
      value: categoryTotals.d4_other_benefits || '0.00',
      icon: Gift,
      bgColor: 'bg-orange-100',
      iconColor: 'text-orange-600',
      subtitle: 'Trips, awards, gifts',
    },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900 truncate">
              Employee BIK ({year})
            </h2>
            <p className="text-xs sm:text-sm text-gray-600 truncate">
              Live per-employee benefits-in-kind &middot; Year of Assessment {year + 1}
            </p>
          </div>

          <div className="flex flex-wrap gap-2 sm:gap-3 flex-shrink-0">
            <IconButton
              icon={RefreshCw}
              onClick={() => refetch()}
              loading={isFetching}
              title="Refresh"
              ariaLabel="Refresh live employee BIK data"
              variant="default"
            />
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
        {summaryCards.map((card, index) => {
          const Icon = card.icon;
          return (
            <div key={index} className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
              <div className="flex items-center">
                <div className={`w-9 h-9 sm:w-12 sm:h-12 ${card.bgColor} rounded-full flex items-center justify-center flex-shrink-0`}>
                  <Icon className={`w-5 h-5 sm:w-6 sm:h-6 ${card.iconColor}`} />
                </div>
                <div className="ml-2 sm:ml-4 min-w-0">
                  <p className="text-lg sm:text-2xl font-bold text-gray-900 tabular-nums truncate">
                    {formatMoney(card.value)}
                  </p>
                  <p className="text-xs sm:text-sm text-gray-600 truncate">{card.title}</p>
                  <p className="hidden sm:block text-xs text-gray-400 truncate">{card.subtitle}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Info note */}
      <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 sm:p-5">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-xs sm:text-sm text-blue-900 space-y-1">
            <p className="font-semibold">Live figures &middot; verify or lock to submit</p>
            <p>
              Each employee's total is computed live from the Asset Portal, dormitory
              assignments and one-time benefits &mdash; edit the source and it updates here
              automatically. Open a row to see the per-item calculation.
            </p>
            <p>
              When the year's numbers are final, <span className="font-medium">Verify</span> then
              <span className="font-medium"> Lock</span> an employee to freeze the figure for IR8A
              (Appendix 8A &rarr; Form IR8A item d8). Frozen rows flag if the source later changes.
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl shadow-sm border p-4">
        <div className="flex items-center gap-3">
          <DebouncedSearchInput
            value={searchTerm}
            onSearch={setSearchTerm}
            placeholder="Search by employee..."
            className="max-w-xl"
          />
          <FilterButton
            theme="admin"
            isOpen={showFilters}
            activeCount={filterStatus ? 1 : 0}
            onClick={() => setShowFilters(v => !v)}
          />
        </div>

        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="form-label-admin">Status</label>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="select-admin w-full"
                >
                  <option value="">All Statuses</option>
                  <option value="live">Live (not frozen)</option>
                  <option value="verified">Verified</option>
                  <option value="locked">Locked for IR8A</option>
                </select>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* List */}
      {filteredRows.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm border px-6 py-12 text-center text-gray-500">
          <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p>No employees with BIK for {year}</p>
          <p className="text-sm mt-1">
            BIK appears here automatically when a vehicle, dormitory or one-time benefit is recorded.
          </p>
        </div>
      ) : (
        <>
          {/* Mobile: stacked cards */}
          <div className="md:hidden space-y-3">
            {filteredRows.map((row) => (
              <div
                key={row.staff_id}
                className="border border-gray-200 rounded-xl p-4 bg-white cursor-pointer active:bg-gray-50 flex flex-col h-full"
                onClick={() => setDetailStaffId(row.staff_id)}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {row.employee_name || row.staff_id}
                    </p>
                    <p className="text-xs text-gray-500">{row.staff_id}</p>
                  </div>
                  <div className="flex-shrink-0 flex items-center gap-1">
                    {row.frozen_differs && (
                      <AlertTriangle className="w-4 h-4 text-amber-500" title="Source changed since frozen" />
                    )}
                    <StatusBadge status={row.status} />
                  </div>
                </div>
                <div className="mt-3 space-y-2 flex-1">
                  <div className="flex items-start justify-between gap-3 text-sm">
                    <span className="text-gray-500 flex-shrink-0">Accommodation</span>
                    <span className="text-gray-900 text-right tabular-nums">{formatMoney(row.d2_accommodation_value)}</span>
                  </div>
                  <div className="flex items-start justify-between gap-3 text-sm">
                    <span className="text-gray-500 flex-shrink-0">One-Time</span>
                    <span className="text-gray-900 text-right tabular-nums">{formatMoney(row.d4_other_benefits_value)}</span>
                  </div>
                  <div className="flex items-start justify-between gap-3 text-sm">
                    <span className="text-gray-500 flex-shrink-0">Car Benefit</span>
                    <span className="text-gray-900 text-right tabular-nums">{formatMoney(row.d5_motor_vehicle_value)}</span>
                  </div>
                  <div className="flex items-start justify-between gap-3 text-sm border-t border-gray-100 pt-2">
                    <span className="text-gray-500 flex-shrink-0">Total est. BIK</span>
                    <span className="text-gray-900 text-right tabular-nums font-semibold">{formatMoney(row.total_bik_value)}</span>
                  </div>
                </div>
                <div className="card-action-footer">
                  <button
                    onClick={(e) => { e.stopPropagation(); setDetailStaffId(row.staff_id); }}
                    className="icon-button-admin"
                    title="View BIK statement"
                    aria-label={`View BIK statement for ${row.employee_name || row.staff_id}`}
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop: full table */}
          <div className="hidden md:block bg-white rounded-2xl shadow-sm border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="table-header">
                  <tr>
                    <th className="table-header-cell">Employee</th>
                    <th className="table-header-cell text-right">Accommodation (d2)</th>
                    <th className="table-header-cell text-right">One-Time (d4)</th>
                    <th className="table-header-cell text-right">Car Benefit (d5)</th>
                    <th className="table-header-cell text-right">Total Est. Annual BIK</th>
                    <th className="table-header-cell text-center">Status</th>
                    <th className="table-header-cell text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredRows.map((row) => (
                    <tr
                      key={row.staff_id}
                      className="table-row-dense cursor-pointer hover:bg-gray-50"
                      onClick={() => setDetailStaffId(row.staff_id)}
                      title="View BIK statement"
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                            <Users className="w-4 h-4 text-gray-500" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {row.employee_name || row.staff_id}
                            </p>
                            <p className="text-xs text-gray-500 truncate">
                              {row.staff_id}
                              {row.employee_department ? ` \u00b7 ${row.employee_department}` : ''}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <span className="text-sm text-gray-900 tabular-nums">{formatMoney(row.d2_accommodation_value)}</span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <span className="text-sm text-gray-900 tabular-nums">{formatMoney(row.d4_other_benefits_value)}</span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <span className="text-sm text-gray-900 tabular-nums">{formatMoney(row.d5_motor_vehicle_value)}</span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <span className="text-sm font-semibold text-gray-900 tabular-nums">{formatMoney(row.total_bik_value)}</span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-center gap-1.5">
                          {row.frozen_differs && (
                            <AlertTriangle className="w-4 h-4 text-amber-500" title="Source changed since frozen — re-verify" />
                          )}
                          <StatusBadge status={row.status} />
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={(e) => { e.stopPropagation(); setDetailStaffId(row.staff_id); }}
                            className="icon-button-admin"
                            title="View BIK statement"
                            aria-label={`View BIK statement for ${row.employee_name || row.staff_id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Employee BIK Statement */}
      {detailStaffId && (
        <EmployeeBIKStatementModal
          staffId={detailStaffId}
          year={year}
          onClose={() => setDetailStaffId(null)}
          onOpenAsset={(assetId) => setDetailAssetId(assetId)}
        />
      )}

      {/* Read-only Asset Portal detail (HR-facing - no edit/assign/transfer) */}
      <AssetDetailModal
        isOpen={detailAssetId != null}
        assetId={detailAssetId}
        onClose={() => setDetailAssetId(null)}
        readOnly
      />
    </div>
  );
};

// =============================================================================
// Shared bits
// =============================================================================

const Money = ({ value, className = '' }) => (
  <span className={`tabular-nums ${className}`}>{formatMoney(value)}</span>
);

const SectionHeader = ({ icon: Icon, title, code, total }) => (
  <div className="flex items-center justify-between gap-3 mb-3">
    <div className="flex items-center gap-2 min-w-0">
      <Icon className="w-4 h-4 text-gray-500 flex-shrink-0" />
      <h3 className="text-sm font-semibold text-gray-800 truncate">{title}</h3>
      <span className="text-[10px] font-medium text-gray-400 bg-gray-100 rounded px-1.5 py-0.5 flex-shrink-0">{code}</span>
    </div>
    <Money value={total} className="text-sm font-semibold text-gray-900" />
  </div>
);

// =============================================================================
// Employee BIK Statement Modal (live detail)
// =============================================================================

const EmployeeBIKStatementModal = ({ staffId, year, onClose, onOpenAsset }) => {
  const { data: record, isLoading } = useLiveBIKDetail(staffId, year);
  const freezeBIK = useFreezeBIK();

  const breakdown = record?.breakdown || {};
  const accommodation = breakdown.d2_accommodation || [];
  const vehicles = breakdown.d5_motor_vehicle || [];
  const oneTime = breakdown.d4_other_benefits || [];
  const hasAny = accommodation.length > 0 || vehicles.length > 0 || oneTime.length > 0;

  const status = record?.status || 'live';
  const isLocked = status === 'locked';

  const handleFreeze = (action) => {
    freezeBIK.mutate({ staffId, year, action });
  };

  return (
    <Portal>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
        <div className="bg-white rounded-2xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto overscroll-contain">
          {/* Header */}
          <div className="flex items-center justify-between gap-2 px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 sticky top-0 bg-white z-10">
            <div className="min-w-0">
              <h2 className="text-base sm:text-lg font-semibold text-gray-900 truncate">
                BIK Statement{record ? ` \u2014 ${record.employee_name || record.staff_id}` : ''}
              </h2>
              {record && (
                <p className="text-xs sm:text-sm text-gray-600 truncate">
                  {record.staff_id}
                  {record.employee_department ? ` \u00b7 ${record.employee_department}` : ''}
                  {` \u00b7 ${record.year} (YOA ${record.year + 1})`}
                </p>
              )}
            </div>
            <button
              onClick={onClose}
              className="icon-button-admin flex-shrink-0"
              title="Close"
              aria-label="Close BIK statement"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {isLoading || !record ? (
            <div className="flex items-center justify-center py-16">
              <LoadingSpinner size="lg" />
            </div>
          ) : (
            <>
              <div className="p-4 sm:p-6 space-y-6">
                {/* Total + status strip */}
                <div className="bg-gray-50 rounded-xl p-4 flex items-center justify-between gap-3">
                  <div>
                    <p className="text-xs text-gray-500">Total estimated annual BIK</p>
                    <p className="text-2xl font-bold text-gray-900 tabular-nums">{formatMoney(record.total_bik_value)}</p>
                    <p className="text-[11px] text-gray-400 mt-0.5">Feeds IR8A item d8 (Appendix 8A)</p>
                  </div>
                  <div className="text-right">
                    <StatusBadge status={status} showIcon />
                  </div>
                </div>

                {/* Drift banner for frozen records */}
                {record.frozen_differs && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="text-xs text-amber-800">
                      <p className="font-medium">Source data changed since this was {status}.</p>
                      <p>
                        Frozen figure: {formatMoney(record.total_bik_value)} &middot; current live figure:{' '}
                        {formatMoney(record.live_total_bik)}. Re-{isLocked ? 'lock' : 'verify'} to update what will be submitted.
                      </p>
                    </div>
                  </div>
                )}

                {!hasAny && (
                  <div className="text-center text-sm text-gray-500 py-8">
                    No taxable BIK components for {record.year}.
                  </div>
                )}

                {/* Accommodation (d2) */}
                {accommodation.length > 0 && (
                  <div>
                    <SectionHeader icon={Home} title="Accommodation" code="d2" total={record.d2_accommodation_value} />
                    <div className="space-y-2">
                      {accommodation.map((acc, i) => (
                        <div key={i} className="border border-gray-200 rounded-xl p-3">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {acc.dormitory_name || 'Dormitory'}
                              </p>
                              <p className="text-xs text-gray-500">{acc.days_occupied} day(s) occupied</p>
                            </div>
                            <Money value={acc.taxable_value} className="text-sm font-semibold text-gray-900" />
                          </div>
                          <div className="mt-2 text-xs text-gray-500 space-y-0.5">
                            <div className="flex justify-between gap-3">
                              <span>Annual base</span>
                              <Money value={acc.annual_bik_base} />
                            </div>
                            <div className="flex justify-between gap-3">
                              <span>Prorated by occupancy</span>
                              <Money value={acc.prorated_bik} />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Motor Vehicle (d5) */}
                {vehicles.length > 0 && (
                  <div>
                    <SectionHeader icon={Car} title="Motor Vehicle" code="d5" total={record.d5_motor_vehicle_value} />
                    <div className="space-y-2">
                      {vehicles.map((v, i) => (
                        <div key={i} className="border border-gray-200 rounded-xl p-3">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {v.vehicle_number}
                                {(v.vehicle_make || v.vehicle_model) && (
                                  <span className="text-gray-500 font-normal">
                                    {' '}&middot; {v.vehicle_make} {v.vehicle_model}
                                  </span>
                                )}
                              </p>
                              <p className="text-xs text-gray-500">{v.days_assigned} day(s) assigned</p>
                            </div>
                            <Money value={v.prorated_vehicle_bik} className="text-sm font-semibold text-gray-900" />
                          </div>

                          {/* Primary: how the BIK was derived */}
                          <div className="mt-2">
                            <VehicleDerivation
                              derivation={v.derivation}
                              annual={v.annual_vehicle_bik}
                              prorated={v.prorated_vehicle_bik}
                              daysAssigned={v.days_assigned}
                              year={record.year}
                            />
                          </div>

                          {/* Secondary: full asset record */}
                          {v.asset_id != null && (
                            <div className="mt-2 flex justify-end">
                              <button
                                onClick={() => onOpenAsset(v.asset_id)}
                                className="text-xs font-medium text-red-600 hover:text-red-800 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-1 rounded"
                              >
                                View full vehicle record
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* One-Time Benefits (d4) */}
                {oneTime.length > 0 && (
                  <div>
                    <SectionHeader icon={Gift} title="One-Time Benefits" code="d4" total={record.d4_other_benefits_value} />
                    <div className="space-y-2">
                      {oneTime.map((b, i) => (
                        <div key={i} className="border border-gray-200 rounded-xl p-3">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {b.benefit_type_display || b.benefit_type}
                              </p>
                              <p className="text-xs text-gray-500 truncate">
                                {b.benefit_date}{b.description ? ` \u00b7 ${b.description}` : ''}
                              </p>
                            </div>
                            <Money value={b.taxable_value} className="text-sm font-semibold text-gray-900" />
                          </div>
                          <div className="mt-2 text-xs text-gray-500 space-y-1">
                            <div className="flex justify-between gap-3">
                              <span>Benefit value</span>
                              <Money value={b.total_value} />
                            </div>
                            <div className="flex items-center justify-between gap-3">
                              <span>Treatment</span>
                              <StatusBadge status={b.tax_treatment} />
                            </div>
                            {b.tax_treatment_reason && (
                              <p className="text-gray-400">{b.tax_treatment_reason}</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Footer: freeze workflow */}
              <div className="px-4 sm:px-6 py-4 border-t border-gray-200 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3 sticky bottom-0 bg-white">
                <button onClick={onClose} className="btn btn-admin-outline w-full sm:w-auto">
                  Close
                </button>
                <div className="flex items-center gap-2 w-full sm:w-auto">
                  {isLocked ? (
                    record.frozen_differs ? (
                      <button
                        onClick={() => handleFreeze('lock')}
                        disabled={freezeBIK.isPending}
                        className="btn btn-admin-primary flex-1 sm:flex-none"
                      >
                        {freezeBIK.isPending ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Lock className="w-4 h-4 mr-2" />}
                        Re-lock (update)
                      </button>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 text-xs text-green-700">
                        <Lock className="w-3.5 h-3.5" /> Locked for IR8A submission
                      </span>
                    )
                  ) : (
                    <>
                      <button
                        onClick={() => handleFreeze('verify')}
                        disabled={freezeBIK.isPending}
                        className="btn btn-admin-outline flex-1 sm:flex-none"
                      >
                        {freezeBIK.isPending ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                        {status === 'verified' ? 'Re-verify' : 'Verify'}
                      </button>
                      <button
                        onClick={() => handleFreeze('lock')}
                        disabled={freezeBIK.isPending}
                        className="btn btn-admin-primary flex-1 sm:flex-none"
                      >
                        {freezeBIK.isPending ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Lock className="w-4 h-4 mr-2" />}
                        Lock for IR8A
                      </button>
                    </>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </Portal>
  );
};

export default EmployeeBIKTab;
